import { cn } from '@lib/utils';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';


export type ModalAccent = 'green' | 'purple' | 'blue' | 'none';
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

const ACCENT: Record<ModalAccent, string> = {
  green: 'bg-gradient-primary',
  purple: 'bg-gradient-purple',
  blue: 'bg-gradient-blue',
  none: '',
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  size?: ModalSize;
  accent?: ModalAccent;
  /** Hide the top accent bar (default: shown for non-'none' accents). */
  hideAccent?: boolean;
  closeOnOverlay?: boolean;
  children?: ReactNode;
  footer?: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  accent = 'green',
  hideAccent = false,
  closeOnOverlay = true,
  children,
  footer,
}: ModalProps): JSX.Element {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-[90] bg-black/20 backdrop-blur-sm',
            'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-in',
          )}
          onClick={closeOnOverlay ? undefined : (e) => e.preventDefault()}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[100] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            // Cap the height and lay the body out as a flex column so a tall
            // form scrolls INSIDE the modal. Without the cap, `overflow-hidden`
            // silently clips whatever doesn't fit — including the footer, which
            // puts the submit button somewhere the user can never reach.
            'flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden',
            'rounded-3xl bg-white shadow-modal',
            'data-[state=open]:animate-scale-in',
            SIZE[size],
          )}
          onInteractOutside={closeOnOverlay ? undefined : (e) => e.preventDefault()}
        >
          {accent !== 'none' && !hideAccent ? (
            <span aria-hidden className={cn('block h-1 w-full shrink-0', ACCENT[accent])} />
          ) : null}

          {/* min-h-0 lets the scroll area actually shrink; without it a flex
              child refuses to go below its content height and nothing scrolls.
              overflow-y-auto here is a safety valve: if the header and footer
              alone exceed the cap (very short viewport, or a mobile keyboard
              collapsing dvh) the body has already shrunk to nothing, and
              without it the pinned footer would be clipped away again. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-7">
            {title || description ? (
              <div className="mb-5 shrink-0 pr-10">
                {title ? (
                  <Dialog.Title className="text-[20px] font-bold text-apple-label tracking-apple-section">
                    {title}
                  </Dialog.Title>
                ) : null}
                {description ? (
                  <Dialog.Description className="mt-1 text-[14px] text-apple-gray-600">
                    {description}
                  </Dialog.Description>
                ) : null}
              </div>
            ) : null}

            <Dialog.Close
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-xl text-apple-gray-600 transition hover:bg-apple-gray-100 hover:text-apple-label"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </Dialog.Close>

            {/* Negative margin + matching padding buys room on all four sides
                for focus rings (ring-3 + offset) to draw without the scroll
                container clipping them, and clears the 10px webkit scrollbar.
                tabIndex makes the region keyboard-scrollable for modals whose
                body is read-only content with nothing focusable inside. */}
            <div
              tabIndex={0}
              className="-mx-2.5 -my-1 min-h-0 flex-1 overflow-y-auto px-2.5 py-1 text-[15px] text-apple-label"
            >
              {children}
            </div>

            {footer ? (
              <div className="mt-6 flex shrink-0 items-center justify-end gap-3">{footer}</div>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
